import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '../ui/Button';
import { Card, CardHeader } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { CopyButton } from '../components/CopyButton';
import { ArrowLeftIcon, ArrowDownIcon, ArrowUpIcon, ClockIcon, ShieldXIcon, ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
interface TimelineEvent {
  id: string;
  timestamp: string;
  direction: 'ingress' | 'egress';
  method: string;
  decision: 'allowed' | 'denied';
  reason?: string;
  latency?: number;
  policyRule?: string;
  correlationId: string;
  requestPreview?: string;
  responsePreview?: string;
}
const mockSession = {
  id: 'ses_a1b2c3d4',
  serverId: 'srv_8x7k2m9p',
  serverName: 'Production API Gateway',
  startTime: '2024-01-15 14:32:00 UTC',
  endTime: '2024-01-15 14:45:23 UTC',
  clientType: 'Claude Desktop',
  clientVersion: '0.5.2',
  allowedCount: 47,
  deniedCount: 3,
  blockedEgress: 1
};
const mockEvents: TimelineEvent[] = [{
  id: 'evt_1',
  timestamp: '14:32:01.234',
  direction: 'ingress',
  method: 'tools/list',
  decision: 'allowed',
  latency: 12,
  policyRule: 'methods.allowlist',
  correlationId: 'cor_abc123',
  responsePreview: '{"tools": [{"name": "search", ...}, {"name": "create_record", ...}]}'
}, {
  id: 'evt_2',
  timestamp: '14:32:15.567',
  direction: 'ingress',
  method: 'tools/call',
  decision: 'allowed',
  latency: 145,
  policyRule: 'tools.allowlist',
  correlationId: 'cor_def456',
  requestPreview: '{"name": "search", "arguments": {"query": "user data"}}',
  responsePreview: '{"results": [...]}'
}, {
  id: 'evt_3',
  timestamp: '14:33:22.890',
  direction: 'egress',
  method: 'http/request',
  decision: 'denied',
  reason: 'Network egress blocked: api.external.com not in allowlist',
  policyRule: 'network.egress.deny',
  correlationId: 'cor_ghi789',
  requestPreview: '{"url": "https://api.external.com/data", "method": "GET"}'
}, {
  id: 'evt_4',
  timestamp: '14:34:05.123',
  direction: 'ingress',
  method: 'resources/read',
  decision: 'denied',
  reason: 'Method not in allowlist',
  policyRule: 'methods.deny',
  correlationId: 'cor_jkl012',
  requestPreview: '{"uri": "file:///etc/passwd"}'
}, {
  id: 'evt_5',
  timestamp: '14:35:18.456',
  direction: 'ingress',
  method: 'tools/call',
  decision: 'allowed',
  latency: 89,
  policyRule: 'tools.allowlist',
  correlationId: 'cor_mno345',
  requestPreview: '{"name": "create_record", "arguments": {"data": {...}}}',
  responsePreview: '{"id": "rec_xyz", "created": true}'
}];
export function SessionDetail() {
  const {
    sessionId
  } = useParams();
  const [expandedEvent, setExpandedEvent] = useState<string | null>(null);
  const blockedEgressEvents = mockEvents.filter(e => e.direction === 'egress' && e.decision === 'denied');
  return <div data-id="element-454">
      {/* Back Link */}
      <Link to="/audit" className="inline-flex items-center gap-1 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 mb-6" data-id="element-455">
        <ArrowLeftIcon className="h-4 w-4" data-id="element-456" />
        Back to Audit Inbox
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between mb-6" data-id="element-457">
        <div data-id="element-458">
          <div className="flex items-center gap-2" data-id="element-459">
            <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100" data-id="element-460">
              Session Detail
            </h1>
            <span className="text-sm font-mono text-slate-500 dark:text-slate-400" data-id="element-461">
              {mockSession.id}
            </span>
            <CopyButton value={mockSession.id} data-id="element-462" />
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1" data-id="element-463">
            {mockSession.serverName} • {mockSession.startTime}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-464">
        {/* Main Timeline */}
        <div className="lg:col-span-2 space-y-6" data-id="element-465">
          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-4" data-id="element-466">
            <Card className="text-center" data-id="element-467">
              <div className="text-2xl font-semibold text-green-600 dark:text-green-400" data-id="element-468">
                {mockSession.allowedCount}
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400" data-id="element-469">
                Allowed
              </div>
            </Card>
            <Card className="text-center" data-id="element-470">
              <div className="text-2xl font-semibold text-red-600 dark:text-red-400" data-id="element-471">
                {mockSession.deniedCount}
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400" data-id="element-472">
                Denied
              </div>
            </Card>
            <Card className="text-center" data-id="element-473">
              <div className="text-2xl font-semibold text-amber-600 dark:text-amber-400" data-id="element-474">
                {mockSession.blockedEgress}
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400" data-id="element-475">
                Blocked Egress
              </div>
            </Card>
          </div>

          {/* Timeline */}
          <Card data-id="element-476">
            <CardHeader title="Event Timeline" description="Chronological view of all session events" data-id="element-477" />
            <div className="space-y-1" data-id="element-478">
              {mockEvents.map(event => <div key={event.id} className="border border-slate-100 dark:border-slate-800 rounded-md overflow-hidden" data-id="element-479">
                  <button onClick={() => setExpandedEvent(expandedEvent === event.id ? null : event.id)} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors" data-id="element-480">
                    {/* Expand Icon */}
                    {expandedEvent === event.id ? <ChevronDownIcon className="h-4 w-4 text-slate-400 dark:text-slate-500" data-id="element-481" /> : <ChevronRightIcon className="h-4 w-4 text-slate-400 dark:text-slate-500" data-id="element-482" />}

                    {/* Direction */}
                    <div className={`p-1.5 rounded ${event.direction === 'ingress' ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-slate-100 dark:bg-slate-800'}`} data-id="element-483">
                      {event.direction === 'ingress' ? <ArrowDownIcon className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" data-id="element-484" /> : <ArrowUpIcon className="h-3.5 w-3.5 text-slate-600 dark:text-slate-400" data-id="element-485" />}
                    </div>

                    {/* Timestamp */}
                    <span className="text-xs font-mono text-slate-500 dark:text-slate-400 w-24" data-id="element-486">
                      {event.timestamp}
                    </span>

                    {/* Method */}
                    <span className="text-sm font-mono text-slate-900 dark:text-slate-100 flex-1 text-left" data-id="element-487">
                      {event.method}
                    </span>

                    {/* Decision */}
                    <Badge variant={event.decision} data-id="element-488">{event.decision}</Badge>

                    {/* Latency */}
                    {event.latency && <span className="text-xs text-slate-400 dark:text-slate-500 flex items-center gap-1" data-id="element-489">
                        <ClockIcon className="h-3 w-3" data-id="element-490" />
                        {event.latency}ms
                      </span>}
                  </button>

                  {/* Expanded Details */}
                  {expandedEvent === event.id && <div className="px-4 pb-4 pt-2 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-800" data-id="element-491">
                      <dl className="grid grid-cols-2 gap-3 text-xs" data-id="element-492">
                        <div data-id="element-493">
                          <dt className="text-slate-500 dark:text-slate-400" data-id="element-494">
                            Correlation ID
                          </dt>
                          <dd className="font-mono text-slate-900 dark:text-slate-100 flex items-center gap-1" data-id="element-495">
                            {event.correlationId}
                            <CopyButton value={event.correlationId} data-id="element-496" />
                          </dd>
                        </div>
                        <div data-id="element-497">
                          <dt className="text-slate-500 dark:text-slate-400" data-id="element-498">
                            Policy Rule
                          </dt>
                          <dd className="font-mono text-slate-900 dark:text-slate-100" data-id="element-499">
                            {event.policyRule}
                          </dd>
                        </div>
                        {event.reason && <div className="col-span-2" data-id="element-500">
                            <dt className="text-slate-500 dark:text-slate-400" data-id="element-501">
                              Denial Reason
                            </dt>
                            <dd className="text-red-700 dark:text-red-400" data-id="element-502">
                              {event.reason}
                            </dd>
                          </div>}
                        {event.requestPreview && <div className="col-span-2" data-id="element-503">
                            <dt className="text-slate-500 dark:text-slate-400 mb-1" data-id="element-504">
                              Request Preview
                            </dt>
                            <dd className="font-mono text-xs bg-white dark:bg-slate-900 p-2 rounded border border-slate-200 dark:border-slate-800 overflow-x-auto text-slate-900 dark:text-slate-100" data-id="element-505">
                              {event.requestPreview}
                            </dd>
                          </div>}
                        {event.responsePreview && <div className="col-span-2" data-id="element-506">
                            <dt className="text-slate-500 dark:text-slate-400 mb-1" data-id="element-507">
                              Response Preview
                            </dt>
                            <dd className="font-mono text-xs bg-white dark:bg-slate-900 p-2 rounded border border-slate-200 dark:border-slate-800 overflow-x-auto text-slate-900 dark:text-slate-100" data-id="element-508">
                              {event.responsePreview}
                            </dd>
                          </div>}
                      </dl>
                    </div>}
                </div>)}
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6" data-id="element-509">
          {/* Session Info */}
          <Card data-id="element-510">
            <CardHeader title="Session Info" data-id="element-511" />
            <dl className="space-y-3 text-sm" data-id="element-512">
              <div data-id="element-513">
                <dt className="text-slate-500 dark:text-slate-400 text-xs" data-id="element-514">
                  Server
                </dt>
                <dd className="text-slate-900 dark:text-slate-100" data-id="element-515">
                  {mockSession.serverName}
                </dd>
              </div>
              <div data-id="element-516">
                <dt className="text-slate-500 dark:text-slate-400 text-xs" data-id="element-517">
                  Client
                </dt>
                <dd className="text-slate-900 dark:text-slate-100" data-id="element-518">
                  {mockSession.clientType} v{mockSession.clientVersion}
                </dd>
              </div>
              <div data-id="element-519">
                <dt className="text-slate-500 dark:text-slate-400 text-xs" data-id="element-520">
                  Start Time
                </dt>
                <dd className="text-slate-900 dark:text-slate-100 font-mono text-xs" data-id="element-521">
                  {mockSession.startTime}
                </dd>
              </div>
              <div data-id="element-522">
                <dt className="text-slate-500 dark:text-slate-400 text-xs" data-id="element-523">
                  End Time
                </dt>
                <dd className="text-slate-900 dark:text-slate-100 font-mono text-xs" data-id="element-524">
                  {mockSession.endTime}
                </dd>
              </div>
            </dl>
          </Card>

          {/* Blocked Egress */}
          {blockedEgressEvents.length > 0 && <Card className="border-red-200 dark:border-red-900/30 bg-red-50 dark:bg-red-900/20" data-id="element-525">
              <div className="flex items-center gap-2 mb-3" data-id="element-526">
                <ShieldXIcon className="h-4 w-4 text-red-600 dark:text-red-400" data-id="element-527" />
                <span className="text-sm font-semibold text-red-900 dark:text-red-300" data-id="element-528">
                  Blocked Egress
                </span>
              </div>
              <div className="space-y-2" data-id="element-529">
                {blockedEgressEvents.map(event => <div key={event.id} className="text-xs" data-id="element-530">
                    <div className="font-mono text-red-800 dark:text-red-300" data-id="element-531">
                      {event.timestamp}
                    </div>
                    <div className="text-red-700 dark:text-red-400 mt-0.5" data-id="element-532">
                      {event.reason}
                    </div>
                  </div>)}
              </div>
            </Card>}
        </div>
      </div>
    </div>;
}